
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Award } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LeaderboardsProps {
  period: string;
}

const Leaderboards: React.FC<LeaderboardsProps> = ({ period }) => {
  const { t } = useLanguage();

  const { data, isLoading } = useQuery({
    queryKey: ['leaderboards', period],
    queryFn: async () => {
      // Top Universities by Demand (mock data for now)
      const topUniversities = [
        { name: 'Cairo University', demand: 150 },
        { name: 'Alexandria University', demand: 120 },
        { name: 'Ain Shams University', demand: 95 },
        { name: 'Helwan University', demand: 80 },
        { name: 'Mansoura University', demand: 65 }
      ];

      // Top Owners by Response Rate (mock data)
      const topOwners = [
        { name: 'Ahmed Ali', responseRate: 98.5, responses: 45 },
        { name: 'Sara Mohamed', responseRate: 96.2, responses: 38 },
        { name: 'Omar Hassan', responseRate: 94.8, responses: 52 },
        { name: 'Fatima Nour', responseRate: 93.1, responses: 29 },
        { name: 'Khaled Ibrahim', responseRate: 91.7, responses: 41 }
      ];

      return { topUniversities, topOwners };
    }
  });

  const getIcon = (index: number) => {
    switch (index) {
      case 0: return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 1: return <Medal className="h-4 w-4 text-gray-400" />;
      case 2: return <Award className="h-4 w-4 text-amber-600" />;
      default: return <span className="text-sm font-medium">{index + 1}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="h-8 bg-muted rounded"></div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('top_universities', 'أفضل الجامعات', 'Top Universities')}</CardTitle>
          <CardDescription>
            {t('by_demand', 'حسب الطلب', 'By demand (searches, chats, bookings)')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.topUniversities.map((university, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {getIcon(index)}
                  <span className="font-medium">{university.name}</span>
                </div>
                <Badge variant="secondary">{university.demand}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('top_owners', 'أفضل المالكين', 'Top Owners')}</CardTitle>
          <CardDescription>
            {t('by_response_rate', 'حسب معدل الاستجابة', 'By response rate')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.topOwners.map((owner, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {getIcon(index)}
                  <span className="font-medium">{owner.name}</span>
                </div>
                <div className="text-right">
                  <Badge variant="secondary">{owner.responseRate}%</Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    {owner.responses} {t('responses', 'ردود', 'responses')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Leaderboards;
